import React, { useState } from 'react';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase';

// 스토어 주소 (가입은 앱에서만 받는다)
const APP_STORE_URL = 'https://apps.apple.com/app/ionice/id6760106160';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ionice.app';

// ── 스타일 ────────────────────────────────────────────────────────────────────
const s = {
  root: {
    fontFamily: "'Noto Sans KR', sans-serif",
    backgroundColor: '#0A1220',
    minHeight: '100vh',
  },
  backBar: { padding: '16px 32px' },
  backLink: { color: '#5BC4D8', fontSize: 14, cursor: 'pointer', fontWeight: 500 },
  container: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: 'calc(100vh - 60px)', padding: '24px',
  },
  card: {
    backgroundColor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(91,196,216,0.12)',
    padding: '44px 40px', borderRadius: 20,
    textAlign: 'center', width: '100%', maxWidth: 440,
    boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
  },
  logo: {
    fontSize: 32, fontWeight: 700, color: '#5BC4D8',
    fontFamily: "'Rajdhani', sans-serif", letterSpacing: 2,
  },
  subtitle: { color: '#778899', margin: '6px 0 0', fontSize: 13 },

  // 탭
  tabRow: {
    display: 'flex', borderRadius: 10, overflow: 'hidden',
    border: '1px solid rgba(91,196,216,0.15)',
    margin: '28px 0 28px',
  },
  tab: (active) => ({
    flex: 1, padding: '11px 0', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', border: 'none', transition: 'all 0.2s',
    background: active ? 'linear-gradient(135deg, #5BC4D8, #4AABB8)' : 'transparent',
    color: active ? '#0A1220' : '#556677',
    letterSpacing: 0.5,
  }),

  // 폼
  formGroup: { marginBottom: 16, textAlign: 'left' },
  row: { display: 'flex', gap: 12 },
  label: {
    fontSize: 11, color: '#8899AA', fontWeight: 700,
    letterSpacing: 0.8, marginBottom: 6, display: 'block',
    textTransform: 'uppercase',
  },
  inputWrap: {
    display: 'flex', alignItems: 'center',
    backgroundColor: 'rgba(10,18,32,0.8)',
    border: '1px solid rgba(91,196,216,0.1)',
    borderRadius: 9, overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  inputIcon: {
    padding: '0 12px', color: '#4AABB8', fontSize: 15,
    borderRight: '1px solid rgba(91,196,216,0.08)',
    height: 46, display: 'flex', alignItems: 'center',
  },
  input: {
    flex: 1, padding: '0 14px', height: 46,
    border: 'none', outline: 'none',
    backgroundColor: 'transparent',
    color: '#E8ECF1', fontSize: 14,
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  eyeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#556677', padding: '0 12px', fontSize: 14,
  },

  // 구분선
  divider: {
    display: 'flex', alignItems: 'center', gap: 12,
    margin: '20px 0', color: '#334455', fontSize: 12,
  },
  dividerLine: { flex: 1, height: 1, backgroundColor: 'rgba(91,196,216,0.08)' },

  // 버튼
  button: {
    width: '100%', padding: 15, borderRadius: 9, border: 'none',
    background: 'linear-gradient(135deg, #5BC4D8, #4AABB8)',
    color: '#0A1220', fontSize: 15, fontWeight: 700,
    cursor: 'pointer', letterSpacing: 0.5,
    boxShadow: '0 4px 20px rgba(91,196,216,0.25)',
    marginTop: 4, transition: 'opacity 0.2s',
  },
  resetLink: {
    color: '#5BC4D8', fontSize: 13, cursor: 'pointer',
    background: 'none', border: 'none',
    marginTop: 16, display: 'block', width: '100%', textAlign: 'center',
  },

  // 에러/성공
  error: {
    color: '#F87171', fontSize: 13, margin: '0 0 14px',
    backgroundColor: 'rgba(248,113,113,0.08)',
    padding: '10px 14px', borderRadius: 8,
    border: '1px solid rgba(248,113,113,0.2)',
    textAlign: 'left',
  },
  success: {
    color: '#34D399', fontSize: 13, margin: '0 0 14px',
    backgroundColor: 'rgba(52,211,153,0.08)',
    padding: '10px 14px', borderRadius: 8,
    border: '1px solid rgba(52,211,153,0.2)',
    textAlign: 'left',
  },
  hint: { color: '#445566', fontSize: 11, marginTop: 20, lineHeight: 1.7 },
};

// ── 입력 필드 컴포넌트 ────────────────────────────────────────────────────────
function Field({ label, icon, type = 'text', placeholder, value, onChange, required, maxLength, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={s.formGroup}>
      {label && <label style={s.label}>{label}</label>}
      <div style={{ ...s.inputWrap, borderColor: focused ? 'rgba(91,196,216,0.4)' : 'rgba(91,196,216,0.1)' }}>
        {icon && <span style={s.inputIcon}>{icon}</span>}
        <input
          type={type} placeholder={placeholder} value={value}
          onChange={onChange} required={required} maxLength={maxLength}
          style={s.input}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        {children}
      </div>
    </div>
  );
}

// ── 비밀번호 필드 ─────────────────────────────────────────────────────────────
function PasswordField({ label, placeholder, value, onChange, required }) {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <div style={s.formGroup}>
      {label && <label style={s.label}>{label}</label>}
      <div style={{ ...s.inputWrap, borderColor: focused ? 'rgba(91,196,216,0.4)' : 'rgba(91,196,216,0.1)' }}>
        <span style={s.inputIcon}>🔒</span>
        <input
          type={show ? 'text' : 'password'}
          placeholder={placeholder} value={value}
          onChange={onChange} required={required}
          style={s.input}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        <button type="button" style={s.eyeBtn} onClick={() => setShow(v => !v)}>
          {show ? '🙈' : '👁'}
        </button>
      </div>
    </div>
  );
}

// ── 로그인 폼 ────────────────────────────────────────────────────────────────
function LoginForm({ navigate }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('dashboard');
    } catch (err) {
      const code = err.code;
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential')
        setError('이메일 또는 비밀번호가 올바르지 않습니다.');
      else if (code === 'auth/too-many-requests')
        setError('로그인 시도가 너무 많습니다. 잠시 후 다시 시도해 주세요.');
      else
        setError('로그인 중 오류가 발생했습니다. 다시 시도해 주세요.');
    }
    setLoading(false);
  };

  const handleReset = async () => {
    if (!email) { setError('비밀번호 재설정을 위해 이메일을 먼저 입력해 주세요.'); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true); setError('');
    } catch {
      setError('이메일 주소를 확인해 주세요.');
    }
  };

  return (
    <form onSubmit={handleLogin}>
      {error    && <p style={s.error}>{error}</p>}
      {resetSent && <p style={s.success}>✓ 비밀번호 재설정 링크를 보내드렸습니다. 이메일을 확인하세요.</p>}

      <Field label="이메일" icon="📧" type="email" placeholder="example@email.com"
        value={email} onChange={e => setEmail(e.target.value)} required />
      <PasswordField label="비밀번호" placeholder="비밀번호 입력"
        value={password} onChange={e => setPassword(e.target.value)} required />

      <button type="submit" style={{ ...s.button, opacity: loading ? 0.7 : 1 }} disabled={loading}>
        {loading ? '로그인 중…' : '로그인'}
      </button>
      <button type="button" style={s.resetLink} onClick={handleReset}>
        비밀번호를 잊으셨나요?
      </button>
    </form>
  );
}

function Login({ navigate }) {
  return (
    <div style={s.root}>
      <link href="https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Noto+Sans+KR:wght@300;400;500;700&display=swap" rel="stylesheet" />
      <div style={s.backBar}>
        <span onClick={() => navigate('landing')} style={s.backLink}>← ionice.hockey</span>
      </div>
      <div style={s.container}>
        <div style={s.card}>
          {/* 로고 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <span style={{ fontSize: 28 }}>🏒</span>
            <span style={s.logo}>IonIce</span>
          </div>
          <p style={s.subtitle}>아이스하키 클럽 관리 플랫폼</p>

          <LoginForm navigate={navigate} />

          {/* 가입은 앱에서만 받는다. 웹은 운영진 포털이다.
              (생년월일 형식·만 14세 확인·약관 동의 기록을 한 곳에서만 맞추면 된다) */}
          <div style={{
            marginTop: 28, paddingTop: 20, textAlign: 'center',
            borderTop: '1px solid rgba(91,196,216,0.12)',
            fontSize: 13, color: '#778899', lineHeight: 1.7,
          }}>
            <div style={{ fontWeight: 600, color: '#AAB4C0', marginBottom: 4 }}>계정이 없으신가요?</div>
            <div style={{ marginBottom: 14 }}>계정은 IonIce 앱에서 만들어 주세요.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                textDecoration: 'none', color: '#5BC4D8',
                border: '1px solid rgba(91,196,216,0.3)',
                backgroundColor: 'rgba(91,196,216,0.06)',
              }}>App Store</a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                textDecoration: 'none', color: '#5BC4D8',
                border: '1px solid rgba(91,196,216,0.3)',
                backgroundColor: 'rgba(91,196,216,0.06)',
              }}>Google Play</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
